import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

const CSS_Tables = () => {
  return (
    <div className="tutorial-container">
      <h1 className="tutorial-title">📊 CSS Tables</h1>

      <p className="tutorial-intro">
        Tables are used to display data in rows and columns. By default, HTML
        provides the <code>&lt;table&gt;</code>, <code>&lt;tr&gt;</code>,{" "}
        <code>&lt;th&gt;</code>, and <code>&lt;td&gt;</code> elements. CSS lets
        you style tables for better readability and design.
      </p>

      {/* Section 1 */}
      <div className="tutorial-section">
        <h2 className="section-title">🔹 Basic Table</h2>
        <p>
          A simple table is created using <code>&lt;table&gt;</code>,{" "}
          <code>&lt;tr&gt;</code> for rows, <code>&lt;th&gt;</code> for table
          headers, and <code>&lt;td&gt;</code> for table data cells.
        </p>

        <CodeCopyBlock
          code={`<table>
  <tr>
    <th>Name</th>
    <th>Age</th>
    <th>Country</th>
  </tr>
  <tr>
    <td>Abhi</td>
    <td>22</td>
    <td>India</td>
  </tr>
  <tr>
    <td>Emma</td>
    <td>25</td>
    <td>USA</td>
  </tr>
</table>`}
        />

        <div style={{ marginTop: "1rem" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              marginTop: "10px",
            }}
          >
            <thead>
              <tr style={{ background: "#15803d", color: "white" }}>
                <th style={{ border: "1px solid #ccc", padding: "8px" }}>
                  Name
                </th>
                <th style={{ border: "1px solid #ccc", padding: "8px" }}>
                  Age
                </th>
                <th style={{ border: "1px solid #ccc", padding: "8px" }}>
                  Country
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                  Abhi
                </td>
                <td style={{ border: "1px solid #ccc", padding: "8px" }}>22</td>
                <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                  India
                </td>
              </tr>
              <tr>
                <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                  Emma
                </td>
                <td style={{ border: "1px solid #ccc", padding: "8px" }}>25</td>
                <td style={{ border: "1px solid #ccc", padding: "8px" }}>
                  USA
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Section 2 */}
      <div className="tutorial-section">
        <h2 className="section-title">🔹 Styling Tables with CSS</h2>
        <p>
          CSS properties such as <code>border</code>, <code>padding</code>,{" "}
          <code>text-align</code>, <code>background-color</code>, and{" "}
          <code>hover effects</code> make tables look more modern.
        </p>

        <CodeCopyBlock
          code={`table {
  width: 100%;
  border-collapse: collapse;
}

th, td {
  border: 1px solid #ddd;
  padding: 12px;
  text-align: center;
}

th {
  background-color: #15803d;
  color: white;
}

tr:nth-child(even) {
  background-color: #f9f9f9;
}

tr:hover {
  background-color: #d1fae5;
}`}
        />

        <div style={{ marginTop: "1rem" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              boxShadow: "0 0 8px rgba(0,0,0,0.1)",
            }}
          >
            <thead>
              <tr style={{ background: "#15803d", color: "white" }}>
                <th style={{ border: "1px solid #ddd", padding: "12px" }}>
                  Product
                </th>
                <th style={{ border: "1px solid #ddd", padding: "12px" }}>
                  Price
                </th>
                <th style={{ border: "1px solid #ddd", padding: "12px" }}>
                  Stock
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ border: "1px solid #ddd", padding: "12px" }}>
                  Laptop
                </td>
                <td style={{ border: "1px solid #ddd", padding: "12px" }}>
                  $800
                </td>
                <td style={{ border: "1px solid #ddd", padding: "12px" }}>
                  Available
                </td>
              </tr>
              <tr style={{ backgroundColor: "#f9f9f9" }}>
                <td style={{ border: "1px solid #ddd", padding: "12px" }}>
                  Phone
                </td>
                <td style={{ border: "1px solid #ddd", padding: "12px" }}>
                  $500
                </td>
                <td style={{ border: "1px solid #ddd", padding: "12px" }}>
                  Limited
                </td>
              </tr>
              <tr>
                <td style={{ border: "1px solid #ddd", padding: "12px" }}>
                  Headphones
                </td>
                <td style={{ border: "1px solid #ddd", padding: "12px" }}>
                  $50
                </td>
                <td style={{ border: "1px solid #ddd", padding: "12px" }}>
                  Out of Stock
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Section 3 */}
      <div className="tutorial-section">
        <h2 className="section-title">🔹 Advanced Table Features</h2>
        <p>
          You can combine CSS with attributes like <code>colspan</code>,{" "}
          <code>rowspan</code>, and sticky headers for more advanced designs.
        </p>

        <CodeCopyBlock
          code={`<table>
  <tr>
    <th rowspan="2">Name</th>
    <th colspan="2">Marks</th>
  </tr>
  <tr>
    <th>Math</th>
    <th>Science</th>
  </tr>
  <tr>
    <td>Rahul</td>
    <td>90</td>
    <td>85</td>
  </tr>
</table>`}
        />

        <div style={{ marginTop: "1rem", overflowX: "auto" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              textAlign: "center",
            }}
          >
            <thead>
              <tr style={{ background: "#0f766e", color: "white" }}>
                <th
                  rowSpan="2"
                  style={{ border: "1px solid #ddd", padding: "12px" }}
                >
                  Name
                </th>
                <th
                  colSpan="2"
                  style={{ border: "1px solid #ddd", padding: "12px" }}
                >
                  Marks
                </th>
              </tr>
              <tr style={{ background: "#14b8a6", color: "white" }}>
                <th style={{ border: "1px solid #ddd", padding: "12px" }}>
                  Math
                </th>
                <th style={{ border: "1px solid #ddd", padding: "12px" }}>
                  Science
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ border: "1px solid #ddd", padding: "12px" }}>
                  Rahul
                </td>
                <td style={{ border: "1px solid #ddd", padding: "12px" }}>
                  90
                </td>
                <td style={{ border: "1px solid #ddd", padding: "12px" }}>
                  85
                </td>
              </tr>
              <tr style={{ backgroundColor: "#f9f9f9" }}>
                <td style={{ border: "1px solid #ddd", padding: "12px" }}>
                  Sneha
                </td>
                <td style={{ border: "1px solid #ddd", padding: "12px" }}>
                  88
                </td>
                <td style={{ border: "1px solid #ddd", padding: "12px" }}>
                  92
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CSS_Tables;
